import React, { memo } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react';

/**
 * 四色结论卡（2026-09-17 友好性⑤⑥：对齐离线版结论色块语义）。
 * verdict 语义来自生产后链（_is_final_deny 同一函数/拦截闸）：
 *   ACCEPT  绿 — 符合入组条件
 *   DENY    红 — 不符合入组条件
 *   REVIEW  橙 — 建议提交人工审核（非NGS等）
 *   EMPTY   灰 — 未检出变异相关内容/识别内容不足
 * review_note / partner_note 以警告标签列形式贴在卡下（⚠️/ℹ️ 逐行）。
 */

export interface VerdictCardProps {
    verdict?: string;
    reviewNote?: string;
    partnerNote?: string;
}

const VERDICT_META: Record<string, { label: string; color: string; bg: string; border: string; Icon: typeof CheckCircle2 }> = {
    // 2026-09-18 用户令：用词=生产固定词（✅初步符合/⛔初步不符合/🟧需人工审核/📄未检测到变异内容），
    // 判定取自最终定性（后端 _is_final_deny 只读最终定性段，0903d 口径）
    ACCEPT: { label: '初步符合', color: '#16a34a', bg: '#f0fdf4', border: '#86efac', Icon: CheckCircle2 },
    DENY:   { label: '初步不符合', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', Icon: XCircle },
    REVIEW: { label: '需人工审核', color: '#d97706', bg: '#fffbeb', border: '#fcd34d', Icon: AlertTriangle },
    NON_NGS:{ label: '需人工审核', color: '#d97706', bg: '#fffbeb', border: '#fcd34d', Icon: AlertTriangle },
    EMPTY:  { label: '未检测到变异内容', color: '#64748b', bg: '#f8fafc', border: '#cbd5e1', Icon: Info },
};

const VerdictCard: React.FC<VerdictCardProps> = memo(({ verdict, reviewNote, partnerNote }) => {
    const meta = verdict ? VERDICT_META[verdict] : undefined;
    const notes = [reviewNote, partnerNote].filter(Boolean) as string[];
    if (!meta && notes.length === 0) return null;

    return (
        <div className="verdict-card" style={{ marginBottom: 8 }}>
            {meta && (() => {
                const Icon = meta.Icon;
                return (
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '4px 12px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                        color: meta.color, background: meta.bg, border: `1px solid ${meta.border}`,
                    }}>
                        <Icon size={16} />
                        {meta.label}
                    </div>
                );
            })()}
            {notes.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {notes.flatMap((n, bi) =>
                        n.split('\n').filter(l => l.trim()).map((line, li) => {
                            const isInfo = line.trim().startsWith('ℹ️');
                            const color = isInfo ? '#2563eb' : '#d97706';
                            const bg = isInfo ? '#eff6ff' : '#fffbeb';
                            const border = isInfo ? '#bfdbfe' : '#fcd34d';
                            return (
                                <div key={`${bi}-${li}`} style={{
                                    fontSize: 12.5, lineHeight: 1.55, padding: '5px 10px',
                                    borderRadius: 6, color, background: bg,
                                    border: `1px solid ${border}`, whiteSpace: 'pre-wrap',
                                }}>
                                    {line}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
});

VerdictCard.displayName = 'VerdictCard';

export default VerdictCard;
